import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { PlayerView, Resource, ResourceLocation, Seat } from "../types";

export type ResourceInteractionMode = "payment" | "storage";
export type ResourceSlot = ResourceLocation | null;

export interface ResourceBoardInteraction {
  kind: "resources";
  id: string;
  title: string;
  player: PlayerView;
  resources: Resource[];
  mode: ResourceInteractionMode;
  slots: ResourceSlot[];
  onChange: (slots: ResourceSlot[]) => void;
  allowUnstored: boolean;
  incoming: ResourceLocation[];
  singleLocation: boolean;
}

export interface TargetBoardInteraction {
  kind: "regions" | "placed_cards";
  id: string;
  title: string;
  seat: Seat;
  selectedIds: number[];
  allowedIds: number[];
  limit: number;
  onToggle: (id: number) => void;
}

export type BoardInteraction = ResourceBoardInteraction | TargetBoardInteraction;

interface BoardInteractionContextValue {
  interaction: BoardInteraction | null;
  activate: (interaction: BoardInteraction) => void;
  clear: (id: string) => void;
}

const BoardInteractionContext = createContext<BoardInteractionContextValue | null>(null);

export function BoardInteractionProvider({ children }: { children: ReactNode }) {
  const [interaction, setInteraction] = useState<BoardInteraction | null>(null);
  const activate = useCallback((next: BoardInteraction) => setInteraction(next), []);
  const clear = useCallback((id: string) => {
    setInteraction((current) => current?.id === id ? null : current);
  }, []);
  const value = useMemo(() => ({ interaction, activate, clear }), [activate, clear, interaction]);
  return <BoardInteractionContext.Provider value={value}>{children}</BoardInteractionContext.Provider>;
}

export function useBoardInteraction(): BoardInteraction | null {
  return useBoardInteractionContext().interaction;
}

export function useResourceBoardPicker(config: {
  title: string;
  player: PlayerView;
  resources: Resource[];
  mode: ResourceInteractionMode;
  slots: ResourceSlot[];
  onChange: (slots: ResourceSlot[]) => void;
  allowUnstored?: boolean;
  incoming?: ResourceLocation[];
  singleLocation?: boolean;
  autoActivate?: boolean;
}) {
  const id = useId();
  const { interaction, activate, clear } = useBoardInteractionContext();
  const latest = useRef(config);
  latest.current = config;
  const active = interaction?.id === id;
  const signature = resourceInteractionSignature(config);
  const activateCurrent = useCallback(() => {
    const current = latest.current;
    activate({
      kind: "resources",
      id,
      title: current.title,
      player: current.player,
      resources: current.resources,
      mode: current.mode,
      slots: current.slots,
      onChange: current.onChange,
      allowUnstored: current.allowUnstored ?? false,
      incoming: current.incoming ?? [],
      singleLocation: current.singleLocation ?? false,
    });
  }, [activate, id]);
  const autoActivated = useRef(false);

  useEffect(() => {
    if (config.autoActivate && !autoActivated.current) {
      autoActivated.current = true;
      activateCurrent();
    }
  }, [activateCurrent, config.autoActivate]);
  useEffect(() => {
    if (active) activateCurrent();
  }, [active, activateCurrent, signature]);
  useEffect(() => () => clear(id), [clear, id]);

  return { active, activate: activateCurrent };
}

export function useTargetBoardPicker(config: {
  enabled: boolean;
  kind: "regions" | "placed_cards";
  title: string;
  seat: Seat;
  selectedIds: number[];
  allowedIds: number[];
  limit: number;
  onToggle: (id: number) => void;
}) {
  const id = useId();
  const { interaction, activate, clear } = useBoardInteractionContext();
  const latest = useRef(config);
  latest.current = config;
  const active = interaction?.id === id;
  const signature = `${config.enabled}|${config.kind}|${config.title}|${config.seat}|${config.limit}|${config.selectedIds.join(",")}|${config.allowedIds.join(",")}`;
  const activateCurrent = useCallback(() => {
    const current = latest.current;
    if (!current.enabled) return;
    activate({
      kind: current.kind,
      id,
      title: current.title,
      seat: current.seat,
      selectedIds: current.selectedIds,
      allowedIds: current.allowedIds,
      limit: current.limit,
      onToggle: current.onToggle,
    });
  }, [activate, id]);

  useEffect(() => {
    if (config.enabled) activateCurrent();
    else clear(id);
  }, [activateCurrent, clear, config.enabled, id, signature]);
  useEffect(() => () => clear(id), [clear, id]);

  return { active, activate: activateCurrent };
}

function useBoardInteractionContext(): BoardInteractionContextValue {
  const value = useContext(BoardInteractionContext);
  if (!value) throw new Error("BoardInteractionProvider ontbreekt");
  return value;
}

function resourceInteractionSignature(config: Parameters<typeof useResourceBoardPicker>[0]): string {
  const regions = [...config.player.principality.upper_regions, ...config.player.principality.lower_regions]
    .map((region) => `${region.region_id}:${region.amount}:${region.expansion?.counter ?? ""}`)
    .join("|");
  const slots = config.slots.map(resourceLocationKey).join(",");
  return `${config.title}|${config.mode}|${config.resources.join(",")}|${slots}|${regions}|${config.allowUnstored}|${config.singleLocation}|${(config.incoming ?? []).map(resourceLocationKey).join(",")}`;
}

function resourceLocationKey(location: ResourceLocation | null): string {
  if (!location) return "-";
  return location.kind === "region" ? `r:${location.region_id}` : `m:${location.instance_id}`;
}
